(() => {
  if (window.CaffreyEleganza && typeof window.CaffreyEleganza.refresh === 'function') {
    window.CaffreyEleganza.refresh();
    return;
  }

  const state = {
    busy: false,
    host: null,
    observer: null,
    footerObserver: null,
    past: false,
    closing: false,
    mechanismActive: false,
    dialog: null,
    returnFocus: null,
    scrollFrame: null,
  };

  const getRoot = () => document.querySelector('[data-el-root]');
  const reducedMotion = () => (
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth'
  );
  const text = (host, key, fallback = '') => host?.dataset?.[key] || fallback;
  const selected = host => host?.querySelector('[data-el-variant]:checked');
  const options = host => Array.from(host?.querySelectorAll('[data-el-variant]') || []);

  function optionByValue(host, value) {
    if (!host || value == null) return null;
    return options(host).find(option => option.value === String(value)) || null;
  }

  function formFor(host) {
    return host?.querySelector('form[data-el-form]') || null;
  }

  function addButtons(host) {
    const form = formFor(host);
    if (!form) return [];
    const buttons = new Set([
      ...host.querySelectorAll('.cf-el-add'),
      ...host.querySelectorAll('[data-el-add]'),
    ]);
    if (form.id) {
      document.querySelectorAll('[data-el-add]').forEach(button => {
        if (button.getAttribute('form') === form.id) buttons.add(button);
      });
    }
    return Array.from(buttons);
  }

  function sticky() {
    const host = state.host?.isConnected ? state.host : getRoot();
    const bars = Array.from(document.querySelectorAll('[data-el-sticky]'));
    if (!host || !bars.length) {
      bars.forEach(item => { item.hidden = true; });
      return;
    }
    const hidden = !(
      state.past &&
      !state.closing &&
      !state.mechanismActive &&
      !state.busy &&
      !state.dialog &&
      !!host
    );
    bars.forEach(item => { item.hidden = hidden; });
  }

  function measureSticky() {
    const host = state.host?.isConnected ? state.host : getRoot();
    const add = host?.querySelector('.cf-el-add');
    const footer = document.querySelector('footer') || document.querySelector('.footer');
    const mechanism = Array.from(document.querySelectorAll('[data-el-mechanism]')).find(node => { const r = node.getBoundingClientRect(); return r.top < window.innerHeight * .65 && r.bottom > window.innerHeight * .3; });
    const bounds = mechanism?.getBoundingClientRect();
    state.mechanismActive = !!bounds && bounds.top < window.innerHeight * .65 && bounds.bottom > window.innerHeight * .3;
    state.past = !!add && add.getBoundingClientRect().bottom <= 0;
    state.closing = !!footer && footer.getBoundingClientRect().top < window.innerHeight;
    sticky();
  }

  function scheduleSticky() {
    if (state.scrollFrame !== null) return;
    state.scrollFrame = window.requestAnimationFrame(() => {
      state.scrollFrame = null;
      measureSticky();
    });
  }

  function sync(host, updateURL = false) {
    if (!host || !host.isConnected) return;
    const option = selected(host);
    const available = option?.dataset.available === 'true';
    const addLabel = text(host, 'add', 'Add to cart');
    const addingLabel = text(host, 'adding', 'Adding…');
    const soldOutLabel = text(host, 'soldOut', 'Sold out');

    host.querySelectorAll('[data-el-price]').forEach(node => {
      node.textContent = option?.dataset.price || '';
    });
    host.querySelectorAll('[data-el-selected]').forEach(node => {
      const prefix = text(host, 'sizePrefix', '').trim();
      const title = option?.dataset.title || '';
      node.textContent = [prefix, title].filter(Boolean).join(' ');
    });
    addButtons(host).forEach(button => {
      button.disabled = state.busy || !available;
      button.setAttribute('aria-busy', String(state.busy));
      button.textContent = state.busy ? addingLabel : available ? addLabel : soldOutLabel;
    });

    const heroLink = host.querySelector('[data-el-zoom]');
    const heroImage = heroLink?.querySelector('img');
    if (heroLink && heroImage && !host.querySelector('[data-el-gallery]') && option?.dataset.image && heroLink.href !== option.dataset.zoom) {
      heroImage.removeAttribute('srcset');
      heroImage.src = option.dataset.image;
      heroImage.alt = option.dataset.alt || '';
      const zoomImage = host.querySelector('[data-el-zoom-image]');
      if (zoomImage) zoomImage.alt = heroImage.alt;
      heroLink.href = option.dataset.zoom;
    }

    const announcement = host.querySelector('[data-el-selection-status]');
    if (announcement && updateURL && option) announcement.textContent = `${text(host, 'sizePrefix', 'EU')} ${option.dataset.title}, ${option.dataset.price}${available ? '' : ', ' + soldOutLabel}`;

    if (updateURL && option) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        window.history.replaceState(window.history.state, '', url);
      } catch {
      }
    }
  }

  function feedback(host, message, error = false, withLink = false) {
    const output = host?.querySelector('[data-el-feedback]');
    if (!output) return;
    output.replaceChildren(document.createTextNode(message || ''));
    output.dataset.error = String(error);
    if (withLink) {
      const link = document.createElement('a');
      link.href = window.routes?.cart_url || '/cart';
      link.textContent = text(host, 'viewCart', 'View cart');
      output.append(link);
    }
  }

  function validQuantity(form) {
    const input = form?.querySelector('[name="quantity"]');
    if (!input) return true;
    const value = Number(input.value);
    const minimum = Number(input.getAttribute('min') || 1);
    const maximum = Number(input.getAttribute('max') || 99);
    return Number.isInteger(value) && Number.isFinite(minimum) && Number.isFinite(maximum) && value >= minimum && value <= maximum;
  }

  function cartEndpoint(form) {
    const raw = form?.getAttribute('action') || window.routes?.cart_add_url || '/cart/add';
    const url = new URL(raw, window.location.href);
    if (!/\.js$/i.test(url.pathname)) url.pathname += '.js';
    return url.href;
  }

  function closeDialog() {
    const dialog = state.dialog;
    const focus = state.returnFocus;
    state.dialog = null;
    state.returnFocus = null;
    if (dialog?.open) dialog.close();
    if (focus?.isConnected) {
      try { focus.focus({ preventScroll: true }); } catch {  }
    }
    sticky();
  }

  function wireDialog(dialog) {
    if (!dialog || dialog.__cfElBound) return;
    dialog.__cfElBound = true;
    dialog.addEventListener('close', () => {
      if (state.dialog !== dialog) return;
      const focus = state.returnFocus;
      state.dialog = null;
      state.returnFocus = null;
      const active = document.activeElement;
      if (focus?.isConnected && (active === focus || active === document.body || dialog.contains(active))) {
        try { focus.focus({ preventScroll: true }); } catch {  }
      }
      sticky();
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) {
        dialog.close();
      }
    });
  }

  function openZoom(host, link) {
    const dialog = host?.querySelector('dialog[data-el-dialog]');
    const image = dialog?.querySelector('img[data-el-zoom-image]');
    if (!dialog || !image || typeof dialog.showModal !== 'function') return false;
    wireDialog(dialog);
    if (dialog.open) dialog.close();
    const source = link.href;
    const sourceImage = link.querySelector('img');
    image.removeAttribute('srcset');
    image.src = source;
    image.alt = sourceImage?.alt || '';
    state.dialog = dialog;
    state.returnFocus = link;
    try {
      dialog.showModal();
      sticky();
      return true;
    } catch {
      state.dialog = null;
      state.returnFocus = null;
      return false;
    }
  }

  function cleanup() {
    state.observer?.disconnect();
    state.footerObserver?.disconnect();
    state.observer = null;
    state.footerObserver = null;
    state.past = false;
    state.closing = false;
    closeDialog();
    document.querySelectorAll('[data-el-sticky]').forEach(item => { item.hidden = true; });
    if (state.host) state.host.removeAttribute('data-el-ready');
    state.host = null;
    document.documentElement.classList.remove('cf-el-is-ready');
  }


  function initGallery(host) {
    const gallery = host.querySelector('[data-el-gallery]');
    if (!gallery || gallery.dataset.elGalleryReady) return;
    const thumbs = Array.from(gallery.querySelectorAll('[data-el-thumb]'));
    const visual = gallery.querySelector('[data-el-main-visual]');
    const img = visual?.querySelector('img');
    if (!img || !thumbs.length) return;
    let index = 0;
    let transition = 0;
    let outgoing = null;
    let animations = [];
    const show = next => {
      const token = ++transition;
      animations.forEach(animation => animation.cancel());
      outgoing?.remove();
      img.style.visibility = '';
      const direction = next > index ? 1 : -1;
      outgoing = img.cloneNode();
      outgoing.src = img.currentSrc || img.src;
      outgoing.removeAttribute('srcset');
      outgoing.removeAttribute('id');
      outgoing.alt = '';
      outgoing.setAttribute('aria-hidden', 'true');
      outgoing.classList.add('cf-el-outgoing');
      visual.append(outgoing);
      img.style.visibility = 'hidden';
      index = (next + thumbs.length) % thumbs.length;
      const thumb = thumbs[index];
      img.src = thumb.dataset.elImage;
      img.srcset = `${thumb.dataset.elSmall} 480w, ${thumb.dataset.elImage} 960w, ${thumb.href} 1536w`;
      img.alt = thumb.querySelector('img').alt;
      img.height = Number(thumb.dataset.elHeight);
      visual.dataset.elCurrent = thumb.href;
      thumbs.forEach((t, n) => t.setAttribute('aria-current', String(n === index)));
      gallery.querySelector('[data-el-gallery-status]').textContent = `${index + 1} / ${thumbs.length}`;
      thumb.scrollIntoView({block: 'nearest', inline: 'nearest', behavior: 'instant'});
      const animate = () => {
        if (token !== transition) return;
        img.style.visibility = '';
        if (reducedMotion() === 'auto' || typeof img.animate !== 'function') {
          outgoing?.remove(); outgoing = null; return;
        }
        const timing = {duration: 320, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'both'};
        animations = [
          img.animate([{transform: `translateX(${direction * 100}%)`}, {transform: 'translateX(0)'}], timing),
          outgoing.animate([{transform: 'translateX(0)'}, {transform: `translateX(${-direction * 100}%)`}], timing)
        ];
        Promise.all(animations.map(animation => animation.finished.catch(() => {}))).then(() => {
          if (token !== transition) return;
          outgoing?.remove(); outgoing = null;
          animations.forEach(animation => animation.cancel()); animations = [];
        });
      };
      img.decode().then(animate, animate);
    };
    thumbs.forEach((t, n) => t.addEventListener('click', e => {
      if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
      e.preventDefault(); show(n);
    }));
    gallery.querySelector('[data-el-prev]').addEventListener('click', () => show(index - 1));
    gallery.querySelector('[data-el-next]').addEventListener('click', () => show(index + 1));
    gallery.addEventListener('keydown', e => {
      if (!['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
      if (!e.target.closest('[data-el-main-visual], [data-el-thumb], [data-el-prev], [data-el-next]')) return;
      e.preventDefault(); show(index + (e.key === 'ArrowRight' ? 1 : -1));
    });
    let drag = null;
    visual.addEventListener('pointerdown', e => {
      if (!e.isPrimary || e.button !== 0) return;
      drag = {id: e.pointerId, x: e.clientX, y: e.clientY};
      visual.setPointerCapture(e.pointerId);
    });
    visual.addEventListener('pointerup', e => {
      if (!drag || drag.id !== e.pointerId) return;
      const dx = e.clientX - drag.x;
      const dy = e.clientY - drag.y;
      drag = null;
      if (Math.abs(dx) >= Math.max(30, visual.clientWidth * .08) && Math.abs(dx) > Math.abs(dy) * 1.2) {
        show(index + (dx < 0 ? 1 : -1));
      }
    });
    visual.addEventListener('pointercancel', () => { drag = null; });
    visual.addEventListener('lostpointercapture', () => { drag = null; });
    visual.addEventListener('dragstart', e => e.preventDefault());
    gallery.dataset.elGalleryReady = 'true';
  }

  function refresh() {
    const host = getRoot();
    if (!host) {
      cleanup();
      return;
    }
    if (state.host && state.host !== host) closeDialog();
    state.host = host;
    state.observer?.disconnect();
    state.footerObserver?.disconnect();
    state.observer = null;
    state.footerObserver = null;
    state.past = false;
    state.closing = false;
    document.documentElement.classList.add('cf-el-is-ready');
    host.dataset.elReady = 'true';

    const queryVariant = new URL(window.location.href).searchParams.get('variant');
    const urlOption = optionByValue(host, queryVariant);
    if (urlOption) urlOption.checked = true;
    sync(host);
    initGallery(host);

    const add = host.querySelector('.cf-el-add');
    if (add && typeof window.IntersectionObserver === 'function') {
      state.observer = new IntersectionObserver(measureSticky);
      state.observer.observe(add);
    }
    const footer = document.querySelector('footer') || document.querySelector('.footer');
    if (footer && typeof window.IntersectionObserver === 'function') {
      state.footerObserver = new IntersectionObserver(measureSticky);
      state.footerObserver.observe(footer);
    }
    measureSticky();
  }

  async function submit(event) {
    const form = event.target;
    if (!form.matches('[data-el-form]')) return;
    const host = form.closest('[data-el-root]') || getRoot();
    if (!host) return;
    event.preventDefault();
    const option = selected(host);
    if (state.busy) return;
    if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;
    if (!validQuantity(form)) return;
    if (!option || option.dataset.available !== 'true') {
      feedback(host, text(host, 'soldOut', 'Sold out'), true);
      return;
    }

    state.busy = true;
    sync(host);
    sticky();
    feedback(host, '');
    let sent = false;
    try {
      const data = new FormData(form);
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      if (cart?.getSectionsToRender) {
        data.set('sections', cart.getSectionsToRender().map(section => section.id).join(','));
        data.set('sections_url', `${window.location.pathname}${window.location.search}`);
        cart.setActiveElement?.(document.activeElement);
      }
      sent = true;
      const response = await fetch(cartEndpoint(form), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: data,
      });
      let result;
      try {
        result = await response.json();
      } catch {
        if (!response.ok) {
          feedback(host, text(host, 'error', 'Unable to add this item.'), true);
        } else {
          feedback(host, text(host, 'checkCart', 'Please check your cart.'), true, true);
        }
        return;
      }
      if (!response.ok || result?.status) {
        feedback(host, result?.description || result?.message || text(host, 'error', 'Unable to add this item.'), true);
        return;
      }
      feedback(host, text(host, 'added', 'Added to cart'), false, true);
      if (cart?.renderContents && result?.sections) {
        try {
          cart.classList.remove('is-empty');
          cart.renderContents(result);
        } catch {
          window.location.assign(window.routes?.cart_url || '/cart');
        }
      } else {
        window.location.assign(window.routes?.cart_url || '/cart');
      }
    } catch {
      feedback(host, sent ? text(host, 'checkCart', 'Please check your cart.') : text(host, 'error', 'Unable to add this item.'), true, sent);
    } finally {
      state.busy = false;
      if (state.host === host && host.isConnected) sync(host);
      sticky();
    }
  }

  document.addEventListener('change', event => {
    const host = getRoot();
    if (!host || !host.contains(event.target) || !event.target.matches('[data-el-variant]') || !event.target.checked) return;
    feedback(host, '');
    sync(host, true);
  });

  document.addEventListener('click', event => {
    if (state.dialog && event.target === state.dialog) return;

    const close = event.target.closest('[data-el-close]');
    if (close && state.dialog?.contains(close)) {
      event.preventDefault();
      state.dialog.close();
      return;
    }

    const host = getRoot();
    if (!host || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;

    const changeSize = event.target.closest('a[data-el-change-size]');
    if (changeSize && host.contains(changeSize)) {
      const option = selected(host);
      const target = option?.labels?.[0] || option;
      if (target) {
        event.preventDefault();
        target.scrollIntoView({ behavior: reducedMotion(), block: 'center' });
        try { option.focus({ preventScroll: true }); } catch {  }
      }
      return;
    }

    const zoom = event.target.closest('a[data-el-zoom]');
    if (zoom && zoom.closest('.cf-el') && openZoom(host, zoom)) event.preventDefault();
  });

  document.addEventListener('submit', submit);
  window.addEventListener('scroll', scheduleSticky, { passive: true });
  window.addEventListener('resize', scheduleSticky);
  window.addEventListener('popstate', () => {
    const host = getRoot();
    if (!host) return;
    const option = optionByValue(host, new URL(window.location.href).searchParams.get('variant'));
    if (option) {
      option.checked = true;
      feedback(host, '');
      sync(host);
    }
  });

  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:reorder', refresh);
  document.addEventListener('shopify:section:unload', event => {
    const target = event.target;
    const host = state.host;
    if (host && target && (target === host || target.contains?.(host))) cleanup();
    queueMicrotask(refresh);
  });
  document.addEventListener('shopify:block:select', event => {
    event.target.closest('details')?.setAttribute('open', '');
  });

  window.CaffreyEleganza = { refresh };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();
})();
